import { useState, useEffect, useRef } from 'react';

export interface CountdownTime {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

interface UseCountdownOptions {
  startTime: string | number | Date | null | undefined; // 开始时间，ISO 8601 格式字符串、时间戳或 Date 对象
  durationDays?: number; // 倒计时持续天数，默认 3 天
  enabled?: boolean; // 是否启用倒计时，默认 true
}

/**
 * 倒计时 Hook
 * @param options 配置选项
 * @returns 倒计时状态，格式为 { days, hours, minutes, seconds }，如果倒计时未启用或已结束则返回 null
 */
export const useCountdown = (options: UseCountdownOptions): CountdownTime | null => {
  const { startTime, durationDays = 3, enabled = true } = options;
  const [countdown, setCountdown] = useState<CountdownTime | null>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // 如果未启用或没有开始时间，清除倒计时
    if (!enabled || !startTime) {
      setCountdown(null);
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
      return;
    }

    // 解析开始时间
    const startDate = startTime instanceof Date ? startTime : new Date(startTime);
    const endTime = new Date(startDate.getTime() + durationDays * 24 * 60 * 60 * 1000);

    // 计算倒计时的函数
    const calculateCountdown = () => {
      const now = new Date();
      const diff = endTime.getTime() - now.getTime();

      if (diff <= 0) {
        // 倒计时结束
        setCountdown(null);
        if (countdownIntervalRef.current) {
          clearInterval(countdownIntervalRef.current);
          countdownIntervalRef.current = null;
        }
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setCountdown({ days, hours, minutes, seconds });
    };

    // 立即计算一次
    calculateCountdown();

    // 每秒更新一次
    countdownIntervalRef.current = setInterval(calculateCountdown, 1000);

    // 清理函数
    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    };
  }, [startTime, durationDays, enabled]);

  return countdown;
};

